import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { pricingPeriods as periodsApi } from '../../utils/api';
import { Plus, Edit2, Trash2, Clock, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const DAYS = ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'];

const TYPE_CONFIG = {
  hc: { label: 'Heures Creuses', color: '#00d2b4', bg: '#00d2b422' },
  hp: { label: 'Heures Pleines', color: '#f5a623', bg: '#f5a62322' },
};

// ── Modale création/édition période ──────────────────────────
function PeriodModal({ period, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:      period?.name        || '',
    type:      period?.type        || 'hp',
    dateFrom:  period?.date_from   || '',
    dateTo:    period?.date_to     || '',
    dayOfWeek: period?.day_of_week || [],
    timeFrom:  period?.time_from   || '',
    timeTo:    period?.time_to     || '',
    isActive:  period?.is_active   ?? true,
  });
  const [loading, setLoading] = useState(false);

  const toggleDay = (d) => setForm(f => ({
    ...f,
    dayOfWeek: f.dayOfWeek.includes(d)
      ? f.dayOfWeek.filter(x => x !== d)
      : [...f.dayOfWeek, d]
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error('Nom requis');
    if (form.timeFrom && !form.timeTo) return toast.error('Heure de fin requise si heure de début renseignée');
    if (!form.timeFrom && form.timeTo) return toast.error('Heure de début requise si heure de fin renseignée');
    setLoading(true);
    try {
      if (period) await periodsApi.update(period.id, form);
      else        await periodsApi.create(form);
      toast.success(period ? 'Période mise à jour' : 'Période créée');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setLoading(false); }
  };

  const f = k => ({ value: form[k], onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const typeConf = TYPE_CONFIG[form.type];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <h3 className="modal-title">{period ? 'Modifier la période' : 'Nouvelle période tarifaire'}</h3>
        <form onSubmit={submit}>

          {/* Type HC / HP */}
          <div className="form-group">
            <label className="form-label">Type *</label>
            <div style={{ display: 'flex', gap: 10 }}>
              {Object.entries(TYPE_CONFIG).map(([t, conf]) => (
                <button key={t} type="button"
                  onClick={() => setForm(p => ({ ...p, type: t }))}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
                    border: `2px solid ${form.type === t ? conf.color : 'var(--border)'}`,
                    background: form.type === t ? conf.bg : 'var(--bg-base)',
                    color: form.type === t ? conf.color : 'var(--text-2)',
                    transition: 'all 0.15s',
                  }}>
                  {conf.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nom */}
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="input" required {...f('name')}
              placeholder={form.type === 'hc' ? 'Ex: Heures creuses semaine' : 'Ex: Week-ends, Vacances Noël…'}/>
          </div>

          {/* Plage de dates */}
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={13}/> Plage de dates
              <span className="text-xs text-muted" style={{ fontWeight: 400 }}>(vide = toute l'année)</span>
            </div>
            <div className="form-row" style={{ marginBottom: 0 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Du</label>
                <input type="date" className="input" {...f('dateFrom')}/>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Au</label>
                <input type="date" className="input" {...f('dateTo')}/>
              </div>
            </div>
          </div>

          {/* Jours de la semaine */}
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
              Jours de la semaine
              <span className="text-xs text-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                (aucun = tous les jours)
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {DAYS.map((d, i) => (
                <button key={i} type="button" onClick={() => toggleDay(i)}
                  style={{
                    flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 600,
                    border: '1px solid var(--border)',
                    background: form.dayOfWeek.includes(i) ? typeConf.color : 'var(--bg-base)',
                    color: form.dayOfWeek.includes(i) ? '#fff' : 'var(--text-2)',
                    transition: 'all 0.15s',
                  }}>
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Plage horaire */}
          <div style={{ background: 'var(--bg-elevated)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={13}/> Plage horaire
              <span className="text-xs text-muted" style={{ fontWeight: 400 }}>(vide = toute la journée)</span>
            </div>
            <div className="form-row" style={{ marginBottom: 0 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">De</label>
                <input type="time" className="input" {...f('timeFrom')}/>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">À</label>
                <input type="time" className="input" {...f('timeTo')}/>
              </div>
            </div>
          </div>

          {/* Statut */}
          <div className="form-group">
            <label className="form-label">Statut</label>
            <select className="input" value={form.isActive}
              onChange={e => setForm(p => ({ ...p, isActive: e.target.value === 'true' }))}>
              <option value="true">Active</option>
              <option value="false">Désactivée</option>
            </select>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 16, height: 16 }}/> : (period ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
// `embedded` : rendu comme onglet interne (page Tarification).
export default function AdminPricingPeriods({ embedded = false }) {
  const { can } = useAuth();
  if (!can('admin.pricing')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [periods, setPeriods] = useState([]);
  const [modal, setModal]     = useState(null); // null | 'new' | period
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    periodsApi.list().then(r => setPeriods(r.data.periods));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deletePeriod = async () => {
    setDeleting(true);
    try {
      await periodsApi.remove(confirmDelete.id);
      toast.success('Période supprimée');
      setConfirmDelete(null);
      load();
    } catch { toast.error('Erreur'); }
    finally { setDeleting(false); }
  };

  const formatScope = (p) => {
    const parts = [];
    if (p.date_from && p.date_to) {
      const df = new Date(p.date_from + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      const dt = new Date(p.date_to   + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
      parts.push(`${df} → ${dt}`);
    } else if (p.date_from) {
      parts.push(`À partir du ${new Date(p.date_from + 'T12:00:00').toLocaleDateString('fr-FR')}`);
    } else {
      parts.push('Toute l\'année');
    }
    if (p.day_of_week?.length > 0) {
      parts.push(p.day_of_week.map(d => DAYS[d]).join(', '));
    } else {
      parts.push('Tous les jours');
    }
    if (p.time_from && p.time_to) {
      parts.push(`${p.time_from} → ${p.time_to}`);
    }
    return parts.join(' · ');
  };

  // Grouper HC / HP
  const grouped = { hc: periods.filter(p => p.type === 'hc'), hp: periods.filter(p => p.type === 'hp') };

  const newPeriodBtn = (
    <button className="btn btn-primary" onClick={() => setModal('new')}>
      <Plus size={15}/> Nouvelle période
    </button>
  );

  return (
    <>
      {embedded ? (
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>{newPeriodBtn}</div>
      ) : (
        <div className="page-header">
          <div>
            <h2>Périodes tarifaires</h2>
            <p>Définissez les plages HC et HP — les tarifs sont configurés dans les profils tarifaires</p>
          </div>
          {newPeriodBtn}
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'}>
        {periods.length === 0 ? (
          <div className="empty-state card">
            <Calendar size={32} style={{ opacity: 0.3 }}/>
            <p>Aucune période tarifaire configurée</p>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>
              <Plus size={14}/> Créer la première
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {Object.entries(TYPE_CONFIG).map(([type, conf]) => (
              <div key={type}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  paddingBottom: 8, borderBottom: `2px solid ${conf.color}`,
                }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                    background: conf.bg, color: conf.color, letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}>{conf.label}</span>
                  <span className="text-xs text-muted">{grouped[type].length} période(s)</span>
                </div>

                {grouped[type].length === 0 ? (
                  <p className="text-sm text-muted" style={{ paddingLeft: 4 }}>
                    Aucune période {conf.label.toLowerCase()} — les tarifs standards s'appliquent.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {grouped[type].map(period => (
                      <div key={period.id} className="card" style={{
                        padding: '14px 18px',
                        borderLeft: `4px solid ${conf.color}`,
                        opacity: period.is_active ? 1 : 0.5,
                        display: 'flex', alignItems: 'center', gap: 14,
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontWeight: 700, fontSize: 15 }}>{period.name}</span>
                            {!period.is_active && <span className="badge badge-gray">Désactivée</span>}
                          </div>
                          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatScope(period)}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => setModal(period)}>
                            <Edit2 size={13}/> Modifier
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(period)}>
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="card" style={{ marginTop: 24, background: 'rgba(124,92,252,0.05)', borderColor: 'rgba(124,92,252,0.2)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7 }}>
            <strong>Comment ça marche :</strong> définissez ici les plages HC et HP (dates, jours, heures).
            Les tarifs correspondants sont renseignés dans <strong>Profils tarifaires</strong> (colonne HC / HP par tranche).
            Lors d'une réservation, le système détermine automatiquement si le créneau est HC ou HP
            et applique le bon prix.
          </div>
        </div>
      </div>

      {modal && (
        <PeriodModal
          period={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
      {confirmDelete && (
        <ConfirmModal
          title="Supprimer la période"
          message={`Supprimer "${confirmDelete.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={deleting}
          onConfirm={deletePeriod}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}
